import type { DriftAccount } from '@interfaces/driftQueue';

export const MOCK_DRIFT_ACCOUNTS: DriftAccount[] = [
  // Single-breach row — equity drift
  {
    id: 'acc-001',
    portfolioName: 'Growth Portfolio',
    accountNumber: 'ACC-10001',
    householdName: 'Smith Family',
    accountType: 'RRSP',
    custodian: 'National Bank',
    portfolioValue: 245000,
    asOfDate: '2026-05-15',
    isStale: false,
    hasInternational: false,
    breachType: 'single',
    breaches: [{ reason: 'Equity Drift', severity: 1 }],
    cashPositionPercentage: 8,
    assetPositionPercentage: 92,
  },
  // Compound-breach row — multiple distinct reasons
  {
    id: 'acc-002',
    portfolioName: 'Balanced Core',
    accountNumber: 'ACC-10002',
    householdName: 'Johnson Household',
    accountType: 'TFSA',
    custodian: 'TD Direct',
    portfolioValue: 118500,
    asOfDate: '2026-05-14',
    isStale: false,
    hasInternational: false,
    breachType: 'compound',
    breaches: [
      { reason: 'Fixed Income Drift', severity: 1 },
      { reason: 'Equity Drift', severity: 2 },
    ],
    cashPositionPercentage: 12,
    assetPositionPercentage: 88,
  },
  // hasInternational: true — international flag row
  {
    id: 'acc-003',
    portfolioName: 'Global Growth',
    accountNumber: 'ACC-10003',
    householdName: 'Tanaka Family',
    accountType: 'Non-Registered',
    custodian: 'Questrade',
    portfolioValue: 380000,
    asOfDate: '2026-05-13',
    isStale: false,
    hasInternational: true,
    breachType: 'single',
    breaches: [{ reason: 'International Allocation Drift', severity: 2 }],
    cashPositionPercentage: 5,
    assetPositionPercentage: 95,
  },
  // isStale: true — stale data indicator
  {
    id: 'acc-004',
    portfolioName: 'Conservative Income',
    accountNumber: 'ACC-10004',
    householdName: 'Williams Family',
    accountType: 'LIRA',
    custodian: 'RBC DS',
    portfolioValue: 97200,
    asOfDate: '2026-04-30',
    isStale: true,
    hasInternational: false,
    breachType: 'single',
    breaches: [{ reason: 'Bond Duration Drift', severity: 3 }],
    cashPositionPercentage: 20,
    assetPositionPercentage: 80,
  },
  // Duplicate-reason row — verify distinct count logic
  {
    id: 'acc-005',
    portfolioName: 'Multi-Asset Plus',
    accountNumber: 'ACC-10005',
    householdName: 'Garcia-Lopez Household',
    accountType: 'RRSP',
    custodian: 'BMO IL',
    portfolioValue: 532000,
    asOfDate: '2026-05-12',
    isStale: false,
    hasInternational: true,
    breachType: 'compound',
    breaches: [
      { reason: 'Equity Drift', severity: 1 },
      { reason: 'Equity Drift', severity: 1 },
      { reason: 'Cash Threshold Breach', severity: 2 },
    ],
    cashPositionPercentage: 3,
    assetPositionPercentage: 97,
  },
  // Multi-alert compound row — varied custodian & high value
  {
    id: 'acc-006',
    portfolioName: 'Retirement Horizon',
    accountNumber: 'ACC-10006',
    householdName: 'Okonkwo Family',
    accountType: 'LIF',
    custodian: 'CIBC WM',
    portfolioValue: 1240000,
    asOfDate: '2026-05-10',
    isStale: false,
    hasInternational: false,
    breachType: 'compound',
    breaches: [
      { reason: 'Sector Concentration', severity: 1 },
      { reason: 'Fixed Income Drift', severity: 2 },
      { reason: 'Cash Threshold Breach', severity: 3 },
    ],
    cashPositionPercentage: 2,
    assetPositionPercentage: 98,
  },
  // No-breach row — should not appear under needsAction / singleBreach / compoundBreach
  {
    id: 'acc-007',
    portfolioName: 'Index Core',
    accountNumber: 'ACC-10007',
    householdName: 'Patel Family',
    accountType: 'TFSA',
    custodian: 'National Bank',
    portfolioValue: 65000,
    asOfDate: '2026-05-16',
    isStale: false,
    hasInternational: false,
    breachType: 'single',
    breaches: [],
    cashPositionPercentage: 10,
    assetPositionPercentage: 90,
  },
  // Stale + international combination
  {
    id: 'acc-008',
    portfolioName: 'Emerging Markets',
    accountNumber: 'ACC-10008',
    householdName: 'Chen Household',
    accountType: 'Non-Registered',
    custodian: 'Questrade',
    portfolioValue: 203400,
    asOfDate: '2026-04-25',
    isStale: true,
    hasInternational: true,
    breachType: 'compound',
    breaches: [
      { reason: 'EM Allocation Drift', severity: 1 },
      { reason: 'Currency Exposure', severity: 2 },
    ],
    cashPositionPercentage: 7,
    assetPositionPercentage: 93,
  },
  // Varied account type and low value
  {
    id: 'acc-009',
    portfolioName: 'Core Fixed Income',
    accountNumber: 'ACC-10009',
    householdName: 'Thompson Family',
    accountType: 'RESP',
    custodian: 'TD Direct',
    portfolioValue: 34800,
    asOfDate: '2026-05-08',
    isStale: false,
    hasInternational: false,
    breachType: 'single',
    breaches: [{ reason: 'Duration Drift', severity: 2 }],
    cashPositionPercentage: 15,
    assetPositionPercentage: 85,
  },
  // Older asOfDate for sorting verification
  {
    id: 'acc-010',
    portfolioName: 'Dividend Growth',
    accountNumber: 'ACC-10010',
    householdName: 'Martinez Family',
    accountType: 'Non-Registered',
    custodian: 'Scotiabank iTrade',
    portfolioValue: 78900,
    asOfDate: '2026-03-01',
    isStale: true,
    hasInternational: false,
    breachType: 'single',
    breaches: [{ reason: 'Dividend Tilt Drift', severity: 3 }],
    cashPositionPercentage: 18,
    assetPositionPercentage: 82,
  },
];
